import { ApiClient } from '@listening/api-contract/api-client-tag';
import type {
  ListNotesUrlParams,
  NoteResponse,
  NoteUpsertInput as NoteUpsertInputSchema,
} from '@listening/api-contract';
import { Effect, type Schema } from 'effect';

// Row shape: the list endpoint's `NoteResponse` (id, title, body,
// audio_conversion_id, …), derived from the API contract.
export type NoteRow = Schema.Schema.Type<typeof NoteResponse>;

// —— Query keys ——

export const notesQueryKey = ['notes', 'list'] as const;

// —— Effect programs ——

// The ListNotes urlParams minus `cursor_id`, which the pager threads in
// separately (see listNotesPageEffect).
type ListNotesParams = Omit<
  Schema.Schema.Type<typeof ListNotesUrlParams>,
  'cursor_id'
>;

const listNotesPageEffect = (
  params: ListNotesParams & { cursor_id?: string } = {},
) =>
  Effect.flatMap(ApiClient, (api) =>
    api.Note.ListNotes({ query: params }).pipe(
      Effect.map((r) => ({
        items: r.data.notes.map((n) => ({ ...n })),
        nextCursor: r.data.cursor_id ?? undefined,
      })),
    ),
  );

// Cursor-follow until exhausted. The single list query drains all pages so
// every view (filtered by audio_conversion_id client-side via `select`) sees
// every note for the current user.
export const listNotesEffect = (params: ListNotesParams = {}) =>
  Effect.gen(function* () {
    const all: Array<NoteRow> = [];
    let cursor: string | undefined;
    do {
      const page = yield* listNotesPageEffect({ ...params, cursor_id: cursor });
      all.push(...page.items);
      cursor = page.nextCursor;
    } while (cursor);
    return all;
  });

// Single-note upsert input: the contract's NoteUpsertInput schema plus the
// client-only `user_id` (the server derives it from auth, but a callsite in
// notes-service.ts still includes it).
export type NoteUpsertInput = Schema.Schema.Type<
  typeof NoteUpsertInputSchema
> & { user_id?: string };

export const upsertNotesEffect = (notes: ReadonlyArray<NoteUpsertInput>) =>
  Effect.flatMap(ApiClient, (api) =>
    api.Note.UpsertManyNotes({ payload: { notes } }).pipe(
      Effect.map((r) => r.data.map((n) => ({ ...n }))),
    ),
  );

export const deleteNoteEffect = (noteId: string) =>
  Effect.flatMap(ApiClient, (api) =>
    api.Note.DeleteNote({ params: { noteId } }),
  );
